import React from 'react';
import PropTypes from 'prop-types';
import RichText from '../../rich-text/base';

// Utils
import { propLink } from '../../link-handler';
import { ImageProps, RichTextPropTypes } from '../../../prop-types';
import { replaceLineBreakShortCode } from '../../../utils/replace-shortcode-br';
import { consistentString } from '../../../utils/content-formatting';

// Styles
import { sizes } from '../../theme/breakpoint';
import {
  StyledContainer,
  Title,
  Wrapper,
  BannerContent,
  MediaContainer,
  MobileContent,
  DesktopContent,
  StyledCTA,
} from './styles';

const LetsBuildSocialHousingHero = ({ content }) => {
  const { title, contentBlockOne, imageOne, ctaOne } = content;

  const fallbackDimensions = {
    width: sizes.desktop,
    height: (
      (imageOne.file.details.image.height / imageOne.file.details.image.width) *
      sizes.desktop
    ).toFixed(),
  };

  return (
    <Wrapper>
      <StyledContainer>
        <BannerContent>
          <Title>{replaceLineBreakShortCode(title)}</Title>
          {contentBlockOne ? <RichText richText={contentBlockOne} /> : null}
          {ctaOne ? (
            <DesktopContent>
              <StyledCTA
                bg={consistentString(ctaOne.ctaColor)}
                link={ctaOne.link}
              >
                {ctaOne.buttonText}
              </StyledCTA>
            </DesktopContent>
          ) : null}
        </BannerContent>
        {imageOne ? (
          <MediaContainer>
            <picture>
              {/* Upto 512px wide - crop image to a square, focused on the main face */}
              {/* prettier-ignore */}
              <source
                media={`(max-width: ${sizes.mobileLand}px)`}
                srcSet={
                  `${imageOne.file.url}?fm=avif&w=${sizes.mobileLand}&h=${sizes.mobileLand}&fit=scale&f=face&fit=fill 1x,
                   ${imageOne.file.url}?fm=avif&w=${sizes.mobileLand * 2}&h=${sizes.mobileLand * 2}&fit=scale&f=face&fit=fill 2x
                  `}
                type="image/avif"
              />

              {/* prettier-ignore */}
              <source
                media={`(max-width: ${sizes.mobileLand}px)`}
                srcSet={
                  `${imageOne.file.url}?fm=webp&w=${sizes.mobileLand}&h=${sizes.mobileLand}&fit=scale&f=face&fit=fill 1x,
                   ${imageOne.file.url}?fm=webp&w=${sizes.mobileLand * 2}&h=${sizes.mobileLand * 2}&fit=scale&f=face&fit=fill 2x,
                  `}
                type="image/webp"
              />

              {/* Above 512px - landscape image */}
              {/* prettier-ignore */}
              <source
                media={`(min-width: ${sizes.mobileLand}px)`}
                srcSet={
                  `${imageOne.file.url}?fm=avif&w=${sizes.desktop} 1x,
                   ${imageOne.file.url}?fm=avif&w=${sizes.desktop * 2} 2x
                   `}
                type="image/avif"
              />

              {/* prettier-ignore */}
              <source
                media={`(min-width: ${sizes.mobileLand}px)`}
                srcSet={
                  `${imageOne.file.url}?fm=avif&w=${sizes.desktop} 1x,
                   ${imageOne.file.url}?fm=avif&w=${sizes.desktop * 2} 2x
                   `}
                type="image/webp"
              />

              {/* Fallback image */}
              <img
                src={`${imageOne.file.url}?fm=jpg&fl=progressive&q=80`}
                alt={imageOne.description}
                width={fallbackDimensions.width}
                height={fallbackDimensions.height}
              />
            </picture>
          </MediaContainer>
        ) : null}
        {ctaOne ? (
          <MobileContent>
            <BannerContent>
              <StyledCTA
                bg={consistentString(ctaOne.ctaColor)}
                link={ctaOne.link}
              >
                {ctaOne.buttonText}
              </StyledCTA>
            </BannerContent>
          </MobileContent>
        ) : null}
      </StyledContainer>
    </Wrapper>
  );
};

LetsBuildSocialHousingHero.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    contentBlockOne: RichTextPropTypes,
    imageOne: ImageProps,
    ctaOne: PropTypes.shape({
      ctaColor: PropTypes.string.isRequired,
      buttonText: PropTypes.string.isRequired,
      link: propLink.isRequired,
    }),
  }),
};

LetsBuildSocialHousingHero.defaultProps = {
  content: {
    contentBlockOne: null,
    imageOne: null,
    ctaOne: null,
  },
};

export default LetsBuildSocialHousingHero;
